import { validateMusicTrackDefinition } from './MusicDefinition';
import type { MusicSelection } from '../../music/replay/MusicContracts';
import { validateAssetDescriptor } from '../../assets/AssetContracts';

export interface MusicSelectionSource {
  next(signal: AbortSignal): Promise<MusicSelection | null>;
  refresh(id: string, signal: AbortSignal): Promise<MusicSelection | null>;
}

export function parseMusicSelection(value: unknown): MusicSelection | null {
  if (value === null) return null;
  if (!value || typeof value !== 'object') throw new Error('Invalid music selection.');
  const selection = value as MusicSelection;
  if (typeof selection.id !== 'string' || !selection.id.trim() || selection.id.length > 128
    || typeof selection.expiresAt !== 'string' || !Number.isFinite(Date.parse(selection.expiresAt))
    || !selection.track || typeof selection.track.id !== 'string' || typeof selection.track.url !== 'string'
    || selection.track.url.length > 8192) throw new Error('Invalid music selection metadata.');
  validateMusicTrackDefinition(selection.track);
  if (selection.track.descriptor !== undefined) {
    validateAssetDescriptor(selection.track.descriptor);
    if (selection.track.descriptor.mimeType !== 'audio/mpeg'
      || selection.track.descriptor.access.kind !== 'public'
      || selection.track.descriptor.dependencies.length !== 0) {
      throw new Error('Invalid music asset descriptor.');
    }
  }
  const url = new URL(selection.track.url, 'https://music.invalid');
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
    throw new Error('Invalid music asset URL.');
  }
  return Object.freeze({ id: selection.id, expiresAt: selection.expiresAt,
    track: Object.freeze({ ...selection.track, ...(selection.track.descriptor ? {
      descriptor: Object.freeze({ ...selection.track.descriptor,
        dependencies: Object.freeze([]), access: Object.freeze({ kind: 'public' as const }) }),
    } : {}) }) });
}

export class HttpMusicSelectionSource implements MusicSelectionSource {
  constructor(readonly endpoint = '/api/music/selections') {}

  async next(signal: AbortSignal): Promise<MusicSelection | null> {
    return this.#request(this.endpoint, 'POST', signal);
  }

  async refresh(id: string, signal: AbortSignal): Promise<MusicSelection | null> {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      throw new Error('Invalid music selection ID.');
    }
    return this.#request(`${this.endpoint}/${id}`, 'GET', signal);
  }

  async #request(url: string, method: 'POST' | 'GET', signal: AbortSignal): Promise<MusicSelection | null> {
    const response = await fetch(url, { method,
      signal: AbortSignal.any([signal, AbortSignal.timeout(8_000)]),
      headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Music selection failed with HTTP ${response.status}.`);
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || !('selection' in body)) throw new Error('Invalid selection response.');
    return parseMusicSelection(body.selection);
  }
}
